import { useMemo, useState } from "react"
import { useLoaderData, type LoaderFunctionArgs, type ActionFunctionArgs } from "react-router"
import qs from "qs"
import { z } from "zod"
import { toast } from "sonner"

import { fetchAPI, handleResponse, throwIfNotOk } from "@/utils"
import { getOrganisations, getProjectsByOrganisation } from "@/utils/projects"
import type { StoredSession } from "@/components/Filters"
import type {
  ConfigLoaderData,
  UserTag,
  EvidenceCount,
  AutoTag,
  BrandLens,
  DocumentEvidenceCategory,
  EvidenceTheme,
  DocumentClassificationType,
  TimelineConfig,
} from "@/utils/types"

import { RecordListTab } from "./recordlisttab"
import { TimelineTab } from "./timelinetab"
import { EvidenceThemesTab } from "./evidencethemestab"
import { DialogueRolesTab } from "./dialougeroletab"
import { FeaturesTab } from "./featureTab"
import { PanelsTab } from "./panelstab"
import { CaseSearchTab, type CaseSearchConfig } from "./casesearchtab"
import { ChecksTab, type ChecksConfig } from "./checkstab"
import { SummaryThemesTab, type SummaryThemesConfig } from "./summarythemestab"
import { RetentionTab } from "./retentiontab"

import type { FeaturesConfig } from "../shared/SharedFeaturesTab"
import type { PanelsConfig } from "../shared/SharedPanelsTab"

const sessionKey = "brand-lenses-filter-storage"

export interface RecordListConfig {
  foldersViewEnabled?: boolean
  showSearch?: boolean
  summariser?: { enabled: boolean }
  userTags?: UserTag[]
  evidenceCounts?: ExtendedEvidenceCount[]
  autoTags?: AutoTag[]
}

export interface EvidenceThemesConfig {
  enableFilters?: boolean
  enableForUnknownClassifications?: boolean
  filters?: Array<{
    classification: string
    themes: Array<string | null>
  }>
  highlightColors?: { [x: string]: string } | undefined
  hiddenThemes?: { [x: string]: boolean } | undefined
}

export interface LensConfigValue {
  recordList?: RecordListConfig
  timeline?: TimelineConfig
  evidenceThemes?: EvidenceThemesConfig
  features?: FeaturesConfig
  panels?: PanelsConfig
  caseSearch?: CaseSearchConfig
  retention?: UnityViewer.Retention.LensConfig
  checks?: ChecksConfig
  summaryThemes?: SummaryThemesConfig
  availableDialogueRoles?: string[]
}

export interface ApiEvidenceCount {
  name: string
  criteriaLabel: string
  brand?: string
  scope?: string
  criteriaType?: string
  ruleScope?: string
  isDeleted?: boolean
}

export interface ExtendedEvidenceCount extends EvidenceCount {
  criteriaLabel?: string
  color?: string
  background?: string
  showName?: boolean
  showValue?: boolean
  iconType?: string
  icon?: string
}

export interface TagWithDetails {
  tagType: string
  values: Array<{ value: string; name?: string }>
}

export interface TagTypeData {
  name: string
  tagRestriction: string
}

export interface EnhancedConfigData extends ConfigLoaderData {
  evidenceCountData: ApiEvidenceCount[]
  tagDetails: Record<string, TagWithDetails>
  autoTagTypes: string[]
  userTagTypes: string[]
  tagTypeData: TagTypeData[]
  classificationMappings: DocumentEvidenceCategory[]
  documentClassifications: DocumentClassificationType[]
  evidenceThemeData: EvidenceTheme[]
}

const SaveSectionPayloadSchema = z.discriminatedUnion("action", [
  z.object({
    action: z.literal("SAVE_SECTION"),
    organisation: z.string().min(1),
    project: z.string().min(1),
    jsonPath: z.string(),
    value: z.unknown(),
    concurrency: z.union([z.string(), z.number()]),
  }),
  z.object({
    action: z.literal("REMOVE_SECTION"),
    organisation: z.string().min(1),
    project: z.string().min(1),
    jsonPath: z.string(),
    concurrency: z.union([z.string(), z.number()]),
  }),
])

const TABS = [
  { id: "recordList", label: "Record List" },
  { id: "timeline", label: "Timeline" },
  { id: "evidenceThemes", label: "Evidence Themes" },
  { id: "dialogueRoles", label: "Available Dialogue Roles" },
  { id: "features", label: "Features" },
  { id: "panels", label: "Panels" },
  { id: "casesearch", label: "Case Search" },
  { id: "retention", label: "Retention" },
  { id: "checks", label: "Checks" },
  { id: "summarythemes", label: "Summary Themes" },
] as const

type TabId = (typeof TABS)[number]["id"]

async function loader({ params }: LoaderFunctionArgs): Promise<EnhancedConfigData> {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]

  const projects = getProjectsByOrganisation(loadedOrganisation)

  const loadedProject =
    filters.parsed?.project == null ? projects[0] : (projects.find((p) => p === filters.parsed?.project) ?? projects[0])

  if (loadedProject.length === 0 || params.lensCode == null) {
    throw new Error("No project or lens code available")
  }

  const [
    lenses,
    tagData,
    evidenceCountData,
    configResponse,
    classificationMappings,
    evidenceThemeData,
    documentClassifications,
  ] = await Promise.all([
    fetchAPI<BrandLens[]>(`/api/unityviewer/lens/${loadedOrganisation}/${loadedProject}`)
      .then(handleResponse)
      .catch(() => []),

    fetchAPI<Array<{ tagType?: string; value?: string }>>(
      `/api/UnityViewer/tag/${encodeURIComponent(loadedOrganisation)}/${encodeURIComponent(loadedProject)}`,
    )
      .then(handleResponse)
      .catch(() => []),

    fetchAPI<ApiEvidenceCount[]>(
      `/api/unityviewer/evidencecount/${encodeURIComponent(loadedOrganisation)}/${encodeURIComponent(loadedProject)}`,
    )
      .then(handleResponse)
      .catch(() => []),

    fetchAPI<{ value: Record<string, unknown>; concurrency: string }>(
      `/api/unityviewer/lens/configuration/${loadedOrganisation}/${loadedProject}/${params.lensCode}`,
    )
      .then(handleResponse)
      .then((rawResponse) => {
        if (rawResponse?.value == null) return null

        const parsedValue: Record<string, unknown> = {}
        Object.keys(rawResponse.value).forEach((key) => {
          const value = rawResponse.value[key]
          if (typeof value === "string") {
            try {
              parsedValue[key] = JSON.parse(value)
            } catch (e) {
              parsedValue[key] = value
            }
          } else {
            parsedValue[key] = value
          }
        })

        return {
          value: parsedValue as unknown as LensConfigValue,
          concurrency: rawResponse.concurrency,
        }
      })
      .catch(() => null),

    fetchAPI<DocumentEvidenceCategory[]>(
      `/api/unityviewer/documentclassificationevidencetheme/${encodeURIComponent(loadedOrganisation)}/${encodeURIComponent(loadedProject)}${qs.stringify({ lensCode: params.lensCode }, { addQueryPrefix: true, skipNulls: true })}`,
    )
      .then(handleResponse)
      .catch(() => []),

    fetchAPI<EvidenceTheme[]>(`/api/unityviewer/evidencetheme/${loadedOrganisation}/${loadedProject}`)
      .then(handleResponse)
      .catch(() => []),

    fetchAPI<DocumentClassificationType[]>(
      `/api/unityviewer/documentclassification/${encodeURIComponent(loadedOrganisation)}/${encodeURIComponent(loadedProject)}`,
    )
      .then(handleResponse)
      .catch(() => []),
  ])

  const lens = lenses.find((l) => l.code === params.lensCode)
  if (lens == null) {
    throw new Error("Lens not found")
  }

  const tagTypes = [...new Set(tagData.map((item) => item.tagType))].filter((t): t is string => t != null && t !== "")

  const [tagTypeData, tagDetailsArray] = await Promise.all([
    fetchAPI<TagTypeData[]>(
      `/api/unityviewer/tagtype/${encodeURIComponent(loadedOrganisation)}/${encodeURIComponent(loadedProject)}`,
    )
      .then(handleResponse)
      .catch(() =>
        tagTypes.map((tagType) => ({
          name: tagType,
          tagRestriction: tagType.toLowerCase().includes("autotag") ? "autoTag" : "userTag",
        })),
      ),

    Promise.all(
      tagTypes.map(async (tagType) => {
        try {
          const details = await fetchAPI<Array<{ value?: string; name?: string }>>(
            `/api/unityviewer/tag/${encodeURIComponent(loadedOrganisation)}/${encodeURIComponent(loadedProject)}/${encodeURIComponent(tagType)}`,
          ).then(handleResponse)

          return {
            tagType,
            values: details
              .map((item) => ({
                value: item.name ?? item.value ?? "",
                name: item.name,
              }))
              .filter((item) => item.value.length > 0),
          }
        } catch {
          return { tagType, values: [] }
        }
      }),
    ),
  ])

  const tagDetails = Object.fromEntries(tagDetailsArray.map((detail) => [detail.tagType, detail]))

  const autoTagTypes = tagTypeData
    .filter((tag) => tag.tagRestriction.toLowerCase() === "autotag")
    .map((tag) => tag.name)
    .filter(Boolean)

  const userTagTypes = tagTypeData
    .filter((tag) => tag.tagRestriction.toLowerCase() !== "autotag")
    .map((tag) => tag.name)
    .filter(Boolean)

  return {
    loadedOrganisation,
    loadedProject,
    lens,
    evidenceCountData,
    tagTypeData,
    evidenceThemeData,
    existingConfig: configResponse,
    tagDetails,
    autoTagTypes,
    userTagTypes,
    availableTagTypes: tagTypeData.map((tag) => tag.name),
    classificationMappings,
    documentClassifications,
  }
}

async function extractPayload(request: Request): Promise<unknown> {
  const contentType = request.headers.get("content-type")

  if (contentType?.includes("application/json") ?? false) {
    return await request.json()
  }

  const formData = await request.formData()
  return {
    action: formData.get("action"),
    organisation: formData.get("organisation"),
    project: formData.get("project"),
    jsonPath: formData.get("jsonPath"),
    value: formData.get("sectionData"),
    concurrency: formData.get("concurrency"),
  }
}

const actionHandlers = {
  SAVE_SECTION: async (
    payload: Extract<z.infer<typeof SaveSectionPayloadSchema>, { action: "SAVE_SECTION" }>,
    lensCode: string,
  ) => {
    const isNewConfig = payload.concurrency === ""
    const url = `/api/unityviewer/lens/configuration/${payload.organisation}/${payload.project}/${lensCode}`
    if (isNewConfig) {
      const sectionKey = payload.jsonPath.replace("$.", "")
      await fetchAPI(url, {
        method: "POST",
        body: {
          value: { [sectionKey]: payload.value },
          concurrency: "",
        },
      }).then(throwIfNotOk)
    } else {
      await fetchAPI(url, {
        method: "PATCH",
        body: {
          jsonPath: payload.jsonPath,
          value: payload.value,
          concurrency: payload.concurrency,
        },
      }).then(throwIfNotOk)
    }
  },

  REMOVE_SECTION: async (
    payload: Extract<z.infer<typeof SaveSectionPayloadSchema>, { action: "REMOVE_SECTION" }>,
    lensCode: string,
  ) => {
    await fetchAPI(`/api/UnityViewer/Lens/configuration/${payload.organisation}/${payload.project}/${lensCode}`, {
      method: "PUT",
      body: {
        jsonPath: payload.jsonPath,
        applyOperation: "Remove",
        concurrency: payload.concurrency,
      },
    }).then(throwIfNotOk)
  },
}

async function action({ request, params }: ActionFunctionArgs) {
  if (params.lensCode == null) {
    return { action: "unknown", errorMessage: "Missing lens code" }
  }

  const payload = await extractPayload(request)
  const parseResult = SaveSectionPayloadSchema.safeParse(payload)

  if (!parseResult.success) {
    return { action: "unknown", errorMessage: "Invalid request payload" }
  }

  const validatedPayload = parseResult.data

  try {
    if (validatedPayload.action === "SAVE_SECTION") {
      await actionHandlers.SAVE_SECTION(validatedPayload, params.lensCode)
      toast.success("Configuration saved successfully.")
    } else {
      await actionHandlers.REMOVE_SECTION(validatedPayload, params.lensCode)
    }
    return { action: validatedPayload.action, success: true }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Error saving configuration"
    return { action: validatedPayload.action, errorMessage, success: false }
  }
}

export function LensConfiguration() {
  const data = useLoaderData<typeof loader>()
  const [activeTab, setActiveTab] = useState<TabId>("recordList")

  const originalConfig = useMemo(() => data.existingConfig?.value ?? {}, [data.existingConfig?.value])

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex-shrink-0 border-b border-gray-200">
        <div className="flex space-x-4 overflow-x-auto lg:space-x-8">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`border-b-2 px-1 pb-1 text-sm font-medium whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="min-h-0 flex-1"
        key={`${data.loadedProject}-${data.lens.code}-${data.existingConfig?.concurrency ?? "new"}`}
      >
        {activeTab === "recordList" && <RecordListTab data={data} config={originalConfig.recordList ?? {}} />}
        {activeTab === "timeline" && <TimelineTab data={data} config={originalConfig.timeline ?? {}} />}
        {activeTab === "evidenceThemes" && (
          <EvidenceThemesTab data={data} config={originalConfig.evidenceThemes ?? {}} />
        )}
        {activeTab === "dialogueRoles" && (
          <DialogueRolesTab data={data} config={originalConfig.availableDialogueRoles ?? []} />
        )}
        {activeTab === "features" && <FeaturesTab data={data} config={originalConfig.features ?? {}} />}
        {activeTab === "panels" && <PanelsTab data={data} config={originalConfig.panels ?? {}} />}
        {activeTab === "casesearch" && <CaseSearchTab data={data} config={originalConfig.caseSearch ?? []} />}
        {activeTab === "checks" && <ChecksTab data={data} config={originalConfig.checks ?? {}} />}
        {activeTab === "summarythemes" && <SummaryThemesTab data={data} config={originalConfig.summaryThemes ?? {}} />}
        {activeTab === "retention" && <RetentionTab data={data} config={originalConfig.retention ?? {}} />}
      </div>
    </div>
  )
}

LensConfiguration.loader = loader
LensConfiguration.action = action
